/**
 * PrintComponents - Components for PDF export layout
 * Manages first page, last page and page breaks for printing
 */
import React from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';

// Import print-specific images for LastPage only
import drias from '../assets/images/pdf_export/drias.png';
import climathd from '../assets/images/pdf_export/climathd.png';
import icu from '../assets/images/pdf_export/icu.jpg';
import driasFutur from '../assets/images/pdf_export/drias-futur.jpg';
import climadiagEntreprise from '../assets/images/pdf_export/climadiag_entreprise.png';

// Import FirstPage assets
import homeBackgroundBlue from '../assets/images/home_background_blue.png';
import climadiagLogo from '../assets/images/logo_light_climadiag.png';
import meteoFranceLogo from '../assets/images/logo_meteo_france.svg';
import franceLogo from '../assets/images/logo_republique_francaise.svg';
import francesadapte from '../assets/images/france-s-adapte.png';

import QRCode from './QRCode';
import OutlookLabel from './OutlookLabel';
import useTokenReplacement from '../hooks/ui/useTokenReplacement';
import { homePage, OVERSEAS_TERRITORIES } from '../config/settings';
import { selectCurrentEntity } from '../store/slices/navigationSlice';

/**
 * Image store for PDF export - mapping image keys to imported assets
 */
const imgStore = {
  drias,
  climathd,
  climadiagEntreprise,
  icu,
  driasFutur,
};

/**
 * FirstPage component - Cover page for PDF export matching legacy design
 * @param {Object} props - Component props
 * @param {string} props.nom - Entity name for the cover
 * @returns {JSX.Element} First page component for print with complete legacy layout
 */
export const FirstPage = ({ nom }) => {
  const theme = useTheme();
  const replaceTokens = useTokenReplacement();
  const entity = useSelector(selectCurrentEntity);

  // Choose subtitle based on territory
  const isOverseas = OVERSEAS_TERRITORIES.isOverseas(entity?.domaine);
  const subtitle = isOverseas
    ? homePage.printCoverSubtitleOverseas
    : homePage.printCoverSubtitle;

  const date = new Date()
    .toLocaleString('fr-FR', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replaceAll('/', '-');

  return (
    <>
      <Box
        sx={{
          marginTop: '-50px',
          display: 'none',
          '@media print': {
            display: 'block',
          },
          width: '100%',
          maxHeight: '100vh',
          pageBreakInside: 'avoid',
        }}
      >
        <Box
          sx={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '0 200px 200px 0',
            borderColor: `transparent ${theme.palette.secondary.main} transparent transparent`,
            float: 'right',
          }}
        />
        <Box component="img" width="100%" mt={10} src={homeBackgroundBlue} alt="" />
        <Typography
          variant="h4"
          fontWeight="500"
          whiteSpace="pre-wrap"
          textTransform="uppercase"
        >
          {replaceTokens(homePage.subtitle)}
        </Typography>
        <Typography
          variant="h5"
          fontWeight="400"
          whiteSpace="pre-wrap"
        >
          {replaceTokens(subtitle)}
          <OutlookLabel sx={{ ml: 3 }} />
        </Typography>
        <Box
          height="0"
          width="70%"
          my={3}
          borderBottom={`2px solid ${theme.palette.secondary.main}`}
        />
        <Typography variant="h4" component="h1" fontWeight="600" textTransform="uppercase">
          {nom}
        </Typography>
        <Typography variant="h6" fontWeight="500">
          {date}
        </Typography>
        <Box
          sx={{
            width: 0,
            height: 0,
            marginTop: 5,
            marginBottom: 5,
            borderStyle: 'solid',
            borderWidth: '75px 0 0 75px',
            borderColor: `transparent transparent transparent ${theme.palette.secondary.main}`,
          }}
        />
        <Stack direction="row" mt={2} justifyContent="space-between">
          <Box component="img" width={180} height={60} src={climadiagLogo} alt="Climadiag Commune" />
          <Box
            component="img"
            src={francesadapte}
            alt="La France s'adapte"
            sx={{
              display: 'block',
              width: 150,
              height: 53,
              aspectRatio: '1158 / 411',
            }}
          />
          <Box>
            <Box component="img" width={60} src={franceLogo} alt="République Française" />
            <Box component="img" width={60} ml={2} src={meteoFranceLogo} alt="Météo-France" />
          </Box>
        </Stack>
      </Box>

      <PageBreak />
    </>
  );
};

/**
 * LastPage component - Information and links page matching legacy layout
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of information items to display (defaults to printLastPage from settings)
 * @returns {JSX.Element} Last page component for print
 */
export const LastPage = ({ items }) => (
  <Box
    sx={{
      display: 'none',
      '@media print': {
        display: 'block',
      },
      width: '100%',
      pageBreakBefore: 'always',
    }}
  >
    <Typography variant="h4" gutterBottom>
      Quelques autres services climatiques de Météo-France
    </Typography>

    <Stack spacing={3.2}>
      {items.map(({ title = '', content = '', img, link }) => (
        <Stack
          key={title}
          direction="row"
          alignItems="center"
          spacing={3}
        >
          <Box
            component="img"
            src={imgStore[img] || imgStore}
            alt=""
            sx={{
              flexShrink: 0,
              width: 200,
              height: 200,
              objectFit: 'contain',
            }}
          />

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <Typography fontSize={11}>{content}</Typography>
            {link && (
              <Link fontSize={11} href={link}>{link}</Link>
            )}
          </Box>

          {link && (
            <QRCode link={link} sx={{ width: 120, ml: 'auto' }} />
          )}
        </Stack>
      ))}
    </Stack>
  </Box>
);

/**
 * PageBreak component - Forces page break in print mode
 * @returns {JSX.Element} Invisible element that creates page break when printing
 */
export const PageBreak = () => (
  <Box
    sx={{
      pageBreakAfter: 'always',
      '@media screen': {
        display: 'none',
      },
    }}
  />
);

/**
 * PrintContainer component - Wrapper for print content
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child elements to render
 * @param {string} props.className - CSS class name for the container
 * @returns {JSX.Element} Container component for print layout
 */
export const PrintContainer = ({ children, className }) => (
  <Box
    className={className}
    sx={{
      '@media print': {
        '& *': {
          visibility: 'visible !important',
        },
      },
    }}
  >
    {children}
  </Box>
);

FirstPage.displayName = 'FirstPage';
LastPage.displayName = 'LastPage';
PageBreak.displayName = 'PageBreak';
PrintContainer.displayName = 'PrintContainer';

export default {
  FirstPage,
  LastPage,
  PageBreak,
  PrintContainer,
};

/**
 * Print page function - Legacy compatible wrapper for printing
 * @param {Object} params - Print parameters
 * @param {string} params.code - Entity code
 * @param {string} params.name - Entity name
 * @param {string} params.outlook - Current outlook
 * @param {Function} params.showPrint - Print dialog show function
 * @deprecated Use PrintProvider.openPrintDialog instead
 */
export const printPage = ({
  code = '',
  name = '',
  outlook,
  showPrint = () => {},
}) => {
  if (!globalThis.print) {
    // eslint-disable-next-line no-alert
    globalThis.alert('Votre navigateur ne supporte pas l\'impression, ce qui est nécessaire pour l\'export PDF. Veuillez essayer sur un autre navigateur.');
  } else {
    // clean name of commune
    const cleanName = name
      .replaceAll(' ', '_') // Replace all spaces with "_"
      .normalize('NFD') // Normalize to unicode so that accents are unique characters
      .replaceAll(/([\u0300-\u036F]|[^0-9a-zA-Z_-])/g, ''); // Remove unique characters accents and all characters not wanted

    const titleItems = [
      'CLIMADIAG',
      code,
      cleanName,
    ];
    if (outlook) { titleItems.push(outlook); }

    document.title = titleItems.join('_');

    if (localStorage.getItem('CLIMADIAG_hide_tutorial') === 'true') {
      globalThis.print();
    } else {
      showPrint();
    }
  }
};
